import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { inventoryApi } from '../services/api';

interface InventoryItem {
  id: number;
  name: string;
  description?: string;
  category: string;
  unit: string;
  stock_quantity: number;
  reorder_level: number;
  unit_price: number;
  supplier?: string;
}

const Inventory: React.FC = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getAll();
      setItems(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData(document.getElementById('inventory-form') as HTMLFormElement);
      const itemData = {
        name: formData.get('name'),
        description: formData.get('description') || null,
        category: formData.get('category'),
        unit: formData.get('unit'),
        stock_quantity: parseFloat(formData.get('stock_quantity') as string),
        reorder_level: parseFloat(formData.get('reorder_level') as string),
        unit_price: parseFloat(formData.get('unit_price') as string),
        supplier: formData.get('supplier') || null,
      };

      if (editingItem) {
        await inventoryApi.update(editingItem.id, itemData);
      } else {
        await inventoryApi.create(itemData);
      }
      handleCloseDialog();
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save inventory item');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const lowStockItems = items.filter(item => item.stock_quantity <= item.reorder_level);
  const totalValue = items.reduce((sum, item) => sum + (item.stock_quantity * item.unit_price), 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Inventory Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Item
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {lowStockItems.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {lowStockItems.length} item(s) are below reorder level
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Items</Typography>
              <Typography variant="h4">{items.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Low Stock</Typography>
              <Typography variant="h4" color="warning.main">{lowStockItems.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Value</Typography>
              <Typography variant="h4" color="success.main">
                ${totalValue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Total Value</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No inventory items found. Add your first item to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isLowStock = item.stock_quantity <= item.reorder_level;
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <Typography color={isLowStock ? 'error' : 'inherit'}>
                        {item.stock_quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>${item.unit_price.toFixed(2)}</TableCell>
                    <TableCell>${(item.stock_quantity * item.unit_price).toFixed(2)}</TableCell>
                    <TableCell>
                      {isLowStock ? (
                        <Chip label="Low Stock" color="warning" size="small" />
                      ) : (
                        <Chip label="In Stock" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to delete this item?')) {
                            try {
                              await inventoryApi.delete(item.id);
                              fetchItems();
                            } catch (err: any) {
                              setError(err.response?.data?.message || 'Failed to delete item');
                            }
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}</DialogTitle>
        <form id="inventory-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent>
            <TextField
              fullWidth
              name="name"
              label="Item Name"
              margin="normal"
              defaultValue={editingItem?.name || ''}
              required
            />
            <TextField
              fullWidth
              name="description"
              label="Description"
              margin="normal"
              multiline
              rows={2}
              defaultValue={editingItem?.description || ''}
            />
            <TextField
              fullWidth
              name="category"
              label="Category"
              margin="normal"
              defaultValue={editingItem?.category || ''}
              required
            />
            <TextField
              fullWidth
              name="unit"
              label="Unit (e.g., kg, pcs, liters)"
              margin="normal"
              defaultValue={editingItem?.unit || ''}
              required
            />
            <TextField
              fullWidth
              name="stock_quantity"
              label="Stock Quantity"
              type="number"
              margin="normal"
              defaultValue={editingItem?.stock_quantity || 0}
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              fullWidth
              name="reorder_level"
              label="Reorder Level"
              type="number"
              margin="normal"
              defaultValue={editingItem?.reorder_level || 0}
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              fullWidth
              name="unit_price"
              label="Unit Price"
              type="number"
              margin="normal"
              defaultValue={editingItem?.unit_price || 0}
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              fullWidth
              name="supplier"
              label="Supplier"
              margin="normal"
              defaultValue={editingItem?.supplier || ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Inventory;

